import { z } from "zod";

// =============================================================================
// Common Schemas - Reusable building blocks
// =============================================================================

// MongoDB ObjectId validation
export const mongoIdSchema = z
  .string()
  .regex(/^[a-f\d]{24}$/i, "Invalid ID format");

// Email validation
export const emailSchema = z.string().email("Invalid email address");

// Username validation
export const usernameSchema = z
  .string()
  .min(3, "Username must be at least 3 characters")
  .max(20, "Username must be at most 20 characters")
  .regex(/^[a-zA-Z0-9_]+$/, "Only letters, numbers and underscores allowed");

// Positive number
export const positiveNumberSchema = z.number().positive("Must be positive");

// Non-negative number (>= 0)
export const nonNegativeNumberSchema = z
  .number()
  .nonnegative("Must be non-negative");

// ISO date string
export const dateStringSchema = z.string().datetime({ offset: true });

// Date that can be string or Date object
export const flexibleDateSchema = z.union([z.string(), z.date()]);

// Pagination schema for API responses
export const paginationSchema = z.object({
  limit: z.number().int().min(1).max(100),
  offset: z.number().int().min(0),
  total_count: z.number().int().min(0),
});

// Currency code (3 letters)
export const currencyCodeSchema = z
  .string()
  .length(3, "Currency code must be 3 characters")
  .toUpperCase();

// Period options used across the app
export const periodSchema = z.enum(["1d", "1w", "1m", "1y", "max"]);

// Sort order
export const sortOrderSchema = z.enum(["asc", "desc"]);

// =============================================================================
// Helper types
// =============================================================================

export type MongoId = z.infer<typeof mongoIdSchema>;
export type Period = z.infer<typeof periodSchema>;
export type SortOrder = z.infer<typeof sortOrderSchema>;
